import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import router from '@/router';
import { mockColorRules } from './color_rules_mock_data';
import { apiCacheService } from './ApiCacheService';
import { SMART_INVALIDATION_RULES } from '@/config/cacheConfig';

// src/types/global.d.ts
declare global {
  interface Window {
    SHOP_NAME?: string;
  }
}  

const host = process.env.VUE_APP_BACKEND_API_URL;

// build http header
function buildHeader(): { [key: string]: string } {
  return {
    'Content-Type': 'application/json',
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': '*',
    'Accept': 'application/json',
  };
}

function getCurrentUrl() {
  let currentUrl: string = document.location.href;
  if (window.location != window.parent.location) {
    currentUrl = document.referrer;
  }
  return currentUrl;
}

function appendMockMode(url: string): string {
  if (process.env.VUE_APP_API_MODE === 'mock') {
    if (url.includes('?')) {
      return `${url}&api_mode=mock`;
    }
    return `${url}?api_mode=mock`;
  }
  return url;
}

function isEmbbedApp() {
  return getCurrentUrl().includes('admin.shopify.com');
}

// function get Identity Token
export function verifyIdentityToken(): boolean {
  if (isEmbbedApp()) {
    // Use localStorage instead of window.SHOP_NAME (which is lost on page refresh)
    // @ts-ignore: Unreachable code error
    const storeName = window.SHOP_NAME || localStorage.getItem('shopify_store');
    let tokenOptions: any = localStorage.getItem('token_options');
    if (tokenOptions) {

      tokenOptions = JSON.parse(tokenOptions);
      if (storeName && Object.keys(tokenOptions).includes(storeName)) {
        setIdentityToken(
          tokenOptions[storeName].token,
          tokenOptions[storeName].shop_name,
        );
        return true;
      }
    }
    return false;
  }
  return true;
}

export function getIdentityToken() {
  // @ts-ignore: Unreachable code error
  if (isEmbbedApp() || window.SHOP_NAME) {
    const storeName = window.SHOP_NAME || localStorage.getItem('shopify_store');
    let tokenOptions: any = localStorage.getItem('token_options');
    if (tokenOptions) {
      tokenOptions = JSON.parse(tokenOptions);
      if (storeName && Object.keys(tokenOptions).includes(storeName)) {
        setIdentityToken(
          tokenOptions[storeName].token,
          tokenOptions[storeName].shop_name,
        );
      }
    }
  }
  return localStorage.getItem('token') || '';
}

export function setIdentityToken(token: string, storeName: string) {
  const currentStore = localStorage.getItem('shopify_store');
  
  localStorage.setItem('token', token);
  localStorage.setItem('shopify_store', storeName);

  // @ts-ignore: Unreachable code error
  window.SHOP_NAME = storeName;

  let tokenOptions: any = localStorage.getItem('token_options');
  if (tokenOptions) {
    tokenOptions = JSON.parse(tokenOptions);
  } else {
    tokenOptions = {};
  }
  tokenOptions[storeName] = { token, shop_name: storeName };
  localStorage.setItem('token_options', JSON.stringify(tokenOptions));
  
  // Clear cache when switching to a different store
  if (currentStore && currentStore !== storeName) {
    apiCacheService.clearStoreCache(currentStore);
  }
}

export function removeIdentityToken() {
  const storeName = localStorage.getItem('shopify_store') || '';
  let tokenOptions: any = localStorage.getItem('token_options');
  if (tokenOptions) {
    tokenOptions = JSON.parse(tokenOptions);
    if (Object.keys(tokenOptions).includes(storeName)) {
      delete tokenOptions[storeName];
    }
  } else {
    tokenOptions = {};
  }
  localStorage.setItem('token_options', JSON.stringify(tokenOptions));
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
}

export let ax = axios.create({
  baseURL: host,
  headers: buildHeader(),
  timeout: 300000,
  responseType: 'json',
  transformRequest: [
    (data) => {
      return data;
    },
  ],
  transformResponse: [
    (data) => {
      if (data) {
        return data;
      } else {
        return {};
      }
    },
  ],
});

// Helper function to get current store ID for caching
function getCurrentStoreId(): string {
  return localStorage.getItem('shopify_store') || 'default';
}

// Helper function to get base URL pattern from full URL
function getBaseUrlPattern(url: string): string {
  // Remove query parameters and trailing slashes
  const baseUrl = url.split('?')[0].replace(/\/$/, '');
  
  // Extract the base pattern (e.g., /segments/123 -> /segments)
  const segments = baseUrl.split('/');
  if (segments.length > 2) {
    // Return base pattern like /segments, /schedule-reports
    return `/${segments[1]}`;
  }
  return baseUrl;
}

// Smart cache invalidation after successful mutations
function handleCacheInvalidation(url: string, method: string, statusCode: number): void {
  // Only invalidate on successful mutations
  if (method.toUpperCase() === 'GET' || statusCode < 200 || statusCode >= 300) {
    return;
  }

  const storeId = getCurrentStoreId();
  const basePattern = getBaseUrlPattern(url);
  
  // Check if this URL has smart invalidation rules
  const invalidationPatterns = SMART_INVALIDATION_RULES[basePattern as keyof typeof SMART_INVALIDATION_RULES];
  
  if (invalidationPatterns) {
    // Smart invalidation - only clear related patterns
    apiCacheService.clearCacheByPatterns([...invalidationPatterns], storeId);
  }
}

// Cache-aware HTTP request function
function makeGetRequest<T>(url: string, config: any = {}): Promise<T> {
  const storeId = getCurrentStoreId();
  const fullUrl = appendMockMode(url);
  
  // Check cache first for cacheable URLs
  if (apiCacheService.isCacheable(url, 'GET')) {
    const cacheKey = apiCacheService.generateCacheKey(url, 'GET', storeId);
    const cachedEntry = apiCacheService.getCacheEntry(cacheKey);
    
    if (cachedEntry) {
      return Promise.resolve(cachedEntry.data as T);
    }
  }
  
  // Make actual network request
  return ax.get(fullUrl, config).then((res) => {
    // Cache successful responses
    if (apiCacheService.isCacheable(url, 'GET') && res.status >= 200 && res.status < 300) {
      const cacheKey = apiCacheService.generateCacheKey(url, 'GET', storeId);
      apiCacheService.setCacheEntry(cacheKey, res.data, url, 'GET', storeId);
    }
    
    return res.data as T;
  });
}

ax.interceptors.response.use((config) => {
  return config;
}, async (error) => {
  const originalRequest = error.config;
  if (error.response && error.response.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true;
    if (originalRequest.url === '/token/refresh') {
      removeIdentityToken();
      router.push('/');
      return Promise.reject(error);
    }
    try {
      const currentRefreshToken = localStorage.getItem('refresh_token');
      if (!currentRefreshToken) {
        removeIdentityToken();
        router.push('/');
        return Promise.reject(error);
      }
      const response: any = await post('/token/refresh', {
        refresh_token: currentRefreshToken,
      });

      const { token, refresh_token: refreshToken } = response.data;
      setIdentityToken(token, localStorage.getItem('shopify_store') || '');
      localStorage.setItem('refresh_token', refreshToken);
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return ax(originalRequest); // Retry the original request with the new access token.
    } catch (refreshError) {
      removeIdentityToken();
      window.location.reload();
      return Promise.reject(error);
    }
  }
  return Promise.reject(error);
});

/* tslint:disable-next-line */
function processData(data?: any) {
  if (data instanceof FormData) {
    // data.append('token', token)
  } else {
    // data.token = token
  }

  return data;
}

export function get<T>(url: string, data?: any, cancelToken: any = null): Promise<T> {
  const identity: string = getIdentityToken();
  if (ax.defaults.cancelToken && cancelToken === null) {
    ax.defaults.cancelToken = undefined;
  }
  if (cancelToken) {
    ax.defaults.cancelToken = cancelToken;
  }
  
  const config = {
    params: processData(data),
    headers: {
      'Authorization': identity ? `Bearer ${identity}` : '',
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': '*',
    },
  };
  
  return makeGetRequest<any>(url, config)
    .then((res) => {
      if (typeof res === 'string') {
        return JSON.parse(res);
      } else {
        return res;
      }
    });
}

export function post<T>(url: string, data?: any, isForm: boolean = false, headers?: any): Promise<T> {
  const identity: string = getIdentityToken();
  data = processData(data);
  let contentType = 'application/json';
  if (isForm) {
    // contentType = 'application/form-data';
    contentType = 'multipart/form-data';
  } else {
    data = JSON.stringify(data);
  }
  return ax
    .post(appendMockMode(url), data, {
      headers: {
        'Authorization': identity ? `Bearer ${identity}` : '',
        'Content-Type': contentType,
        ...headers,
      },
    })
    .then((res) => {
      // Handle cache invalidation after successful POST
      handleCacheInvalidation(url, 'POST', res.status);
      return JSON.parse(res.data);
    });
}

export function post_with_config<T>(url: string, data?: any, options: AxiosRequestConfig = {}): Promise<T> {
  const identity: string = getIdentityToken();
  let requestData = data;

  // Default headers that will apply if not overridden by options.headers
  // and if data is not FormData (which would have Axios set its own Content-Type)
  const defaultHeaders: Record<string, string> = {
    Authorization: identity ? `Bearer ${identity}` : '',
  };

  // If data is NOT FormData, we assume it should be JSON.
  // Axios will automatically set Content-Type for FormData.
  if (!(data instanceof FormData)) {
    requestData = JSON.stringify(data); // Ensure data is not undefined before stringifying
    defaultHeaders['Content-Type'] = 'application/json';
  }
  // For FormData, Axios handles the Content-Type including the boundary.
  // Do not manually set Content-Type to 'multipart/form-data' here without boundary.

  return ax
    .post(appendMockMode(url), requestData, {
      ...options, // Spread incoming options first
      headers: {
        ...defaultHeaders, // Apply defaults
        ...(options.headers || {}), // Then apply headers from options, allowing override
      },
    })
    .then((res) => {
      if (res.data && typeof res.data === 'string') {
        try {
          return JSON.parse(res.data);
        } catch (e) {
          // Depending on requirements, you might want to throw an error here
          // or return the raw string if that's a possible valid (non-JSON) response.
          // For now, returning raw string if parsing fails.
          return res.data as any;
        }
      }
      return res.data; // If already an object/other type
    })
    .catch((error) => {
        // Log the error with more context if available
        const errorDetails = error.response?.data || error.message || error.toString();
        if (error.response?.data) {
            // Throw an error that includes backend message if possible
            const backendError = new Error(error.response.data.message || 'Backend error during POST request');
            (backendError as any).response = error.response; // Attach full response for further handling
            throw backendError;
        }
        throw error; // Re-throw original error if no backend data
    });
}

export function put<T>(url: string, data?: any): Promise<T> {
  const identity: string = getIdentityToken();
  data = processData(data);
  return ax
    .put(appendMockMode(url), JSON.stringify(data), {
      headers: {
        'Authorization': identity ? `Bearer ${identity}` : '',
        'Content-Type': 'application/json',
      },
    })
    .then((res) => {
      // Handle cache invalidation after successful PUT
      handleCacheInvalidation(url, 'PUT', res.status);
      if (res.data && typeof res.data === 'string') {
        try {
          return JSON.parse(res.data);
        } catch (e) {
          return res.data as any;
        }
      }
      return res.data;
    });
}

export function patch<T>(url: string, data?: any): Promise<T> {
  const identity: string = getIdentityToken();
  data = processData(data);
  return ax
    .patch(appendMockMode(url), JSON.stringify(data), {
      headers: {
        'Authorization': identity ? `Bearer ${identity}` : '',
        'Content-Type': 'application/json',
      },
    })
    .then((res) => {
      // Handle cache invalidation after successful PATCH
      handleCacheInvalidation(url, 'PATCH', res.status);
      
      if (res.data && typeof res.data === 'string') {
        try {
          return JSON.parse(res.data);
        } catch (e) {
          // Depending on requirements, you might want to throw an error here
          // or return the raw string if that's a possible valid (non-JSON) response.
          // For now, returning raw string if parsing fails.
          return res.data as any;
        }
      }
      return res.data; // If already an object/other type
    });
}

export function del<T>(url: string, data?: any): Promise<T> {
  const identity: string = getIdentityToken();
  data = processData(data);
  return ax
    .delete(appendMockMode(url), {
      data: JSON.stringify(data),
      headers: {
        Authorization: identity ? `Bearer ${identity}` : '',
      },
    })
    .then((res) => {
      // Handle cache invalidation after successful DELETE
      handleCacheInvalidation(url, 'DELETE', res.status);
      if (res.data && typeof res.data === 'string') {
        try {
          return JSON.parse(res.data);
        } catch (e) {
          return res.data as any;
        }
      }
      return res.data;
    });
}

export function download(url: string, data?: any): Promise<Blob> {
  const identity: string = getIdentityToken();
  const requestConfig: AxiosRequestConfig = {
    responseType: 'blob', // Important
    headers: {
      Authorization: identity ? `Bearer ${identity}` : '',
    },
    params: data,
  };
  return ax.get(appendMockMode(url), requestConfig)
    .then((response) => {
      return new Blob([response.data]);
    });
}

export async function getColorRules() {
  if (process.env.NODE_ENV === 'development') {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ data: mockColorRules });
      }, 100);
    });
  }

  return get('/api/color-rules');
}

// Cache management functions
export function clearApiCache() {
  apiCacheService.clearAllCache();
}

export function clearStoreCache(storeId?: string) {
  const targetStoreId = storeId || getCurrentStoreId();
  apiCacheService.clearStoreCache(targetStoreId);
}

export function getCacheStats() {
  return apiCacheService.getCacheStats();
}

// Export cache service for advanced usage
export { apiCacheService };
